import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import { toast } from 'react-toastify';
import Dashboard from './Dashboard';
import './admin.css';

const BannerManager = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    mediaType: 'image', // 'image' or 'video'
    mediaFile: null,
    mediaUrl: '',
    tagline: '',
    subtitle: '',
    showButton: false,
    buttonText: '',
    buttonLink: '',
    videoUrl: '',
    priority: 1,
    active: true
  });

  useEffect(() => {
    fetchBanners();
  }, []);

  const fetchBanners = async () => {
    try {
      const bannersSnapshot = await getDocs(collection(db, 'banners'));
      const bannersData = bannersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      // Sort by priority
      bannersData.sort((a, b) => a.priority - b.priority);
      setBanners(bannersData);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching banners:', error);
      toast.error('Failed to load banners');
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, mediaFile: file });
    }
  };

  const uploadMedia = async (file) => {
    const formDataUpload = new FormData();
    formDataUpload.append('image', file);
    formDataUpload.append('project', 'mms'); // Project name
    formDataUpload.append('type', 'banners'); // Type/category

    const response = await fetch('https://imageapi.magicmoonstudios.com/upload.php', {
      method: 'POST',
      body: formDataUpload
    });

    const result = await response.json();
    
    if (result.success && result.url) {
      return result.url;
    } else {
      throw new Error(result.error || 'Upload failed');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.mediaFile && !formData.mediaUrl) {
      toast.error('Please select a media file');
      return;
    }

    if (!formData.tagline) {
      toast.error('Please enter a tagline');
      return;
    }

    setUploading(true);

    try {
      let mediaUrl = formData.mediaUrl;

      // Upload media if a new file is selected
      if (formData.mediaFile) {
        mediaUrl = await uploadMedia(formData.mediaFile);
      }

      const bannerData = {
        mediaType: formData.mediaType,
        mediaUrl: mediaUrl,
        tagline: formData.tagline,
        subtitle: formData.subtitle,
        showButton: formData.showButton,
        buttonText: formData.buttonText,
        buttonLink: formData.buttonLink,
        videoUrl: formData.videoUrl || '',
        priority: parseInt(formData.priority),
        active: formData.active,
        updatedAt: serverTimestamp()
      };

      if (editingBanner) {
        // Update existing banner
        await updateDoc(doc(db, 'banners', editingBanner.id), bannerData);
        toast.success('Banner updated successfully!');
      } else {
        // Add new banner
        bannerData.createdAt = serverTimestamp();
        await addDoc(collection(db, 'banners'), bannerData);
        toast.success('Banner added successfully!');
      }

      // Reset form
      setFormData({
        mediaType: 'image',
        mediaFile: null,
        mediaUrl: '',
        tagline: '',
        subtitle: '',
        showButton: false,
        buttonText: '',
        buttonLink: '',
        videoUrl: '',
        priority: 1,
        active: true
      });
      setShowAddModal(false);
      setEditingBanner(null);
      fetchBanners();
    } catch (error) {
      console.error('Error saving banner:', error);
      toast.error('Failed to save banner: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = (banner) => {
    setEditingBanner(banner);
    setFormData({
      mediaType: banner.mediaType,
      mediaFile: null,
      mediaUrl: banner.mediaUrl,
      tagline: banner.tagline,
      subtitle: banner.subtitle || '',
      showButton: banner.showButton || false,
      buttonText: banner.buttonText || '',
      buttonLink: banner.buttonLink || '',
      videoUrl: banner.videoUrl || '',
      priority: banner.priority,
      active: banner.active
    });
    setShowAddModal(true);
  };

  const handleDelete = async (bannerId, mediaUrl) => {
    if (!window.confirm('Are you sure you want to delete this banner?')) return;

    try {
      // Delete from Firebase
      await deleteDoc(doc(db, 'banners', bannerId));

      // Delete media from API
      if (mediaUrl && mediaUrl.includes('imageapi.magicmoonstudios.com')) {
        await fetch('https://imageapi.magicmoonstudios.com/delete.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ imageUrl: mediaUrl })
        });
      }

      toast.success('Banner deleted successfully!');
      fetchBanners();
    } catch (error) {
      console.error('Error deleting banner:', error);
      toast.error('Failed to delete banner');
    }
  };

  const toggleActive = async (bannerId, currentStatus) => {
    try {
      await updateDoc(doc(db, 'banners', bannerId), {
        active: !currentStatus
      });
      toast.success('Banner status updated!');
      fetchBanners();
    } catch (error) {
      console.error('Error updating banner status:', error);
      toast.error('Failed to update banner status');
    }
  };

  const closeModal = () => {
    setShowAddModal(false);
    setEditingBanner(null);
    setFormData({
      mediaType: 'image',
      mediaFile: null,
      mediaUrl: '',
      tagline: '',
      subtitle: '',
      showButton: false,
      buttonText: '',
      buttonLink: '',
      priority: 1,
      active: true
    });
  };

  return (
    <Dashboard>
      <div className="banner-manager">
        <div className="banner-header">
          <h1>Banner Slider Management</h1>
          <button className="add-banner-btn" onClick={() => setShowAddModal(true)}>
            + Add New Banner
          </button>
        </div>

        {loading ? (
          <div className="loading">Loading banners...</div>
        ) : (
          <div className="banners-list">
            <p className="info-text">
              📌 Banners are displayed in order of priority (1 = first, 2 = second, etc.)
            </p>
            {banners.length === 0 ? (
              <div className="no-banners">
                <p>No banners found. Add your first banner to get started!</p>
                <p className="note">Note: If no banners are added, the default video banner will be shown on the homepage.</p>
              </div>
            ) : (
              <div className="banners-grid">
                {banners.map((banner) => (
                  <div key={banner.id} className="banner-item">
                    <div className="banner-preview">
                      {banner.mediaType === 'video' ? (
                        <video src={banner.mediaUrl} style={{ width: '100%', height: '200px', objectFit: 'cover' }} />
                      ) : (
                        <img src={banner.mediaUrl} alt={banner.tagline} style={{ width: '100%', height: '200px', objectFit: 'cover' }} />
                      )}
                      <div className="banner-overlay">
                        <span className="media-type-badge">{banner.mediaType}</span>
                        <span className="priority-badge">Priority: {banner.priority}</span>
                      </div>
                    </div>
                    <div className="banner-details">
                      <h3>{banner.tagline}</h3>
                      {banner.subtitle && <p className="subtitle">{banner.subtitle}</p>}
                      {banner.showButton && (
                        <p className="button-info">
                          🔘 Button: "{banner.buttonText}" → {banner.buttonLink}
                          {banner.videoUrl && <><br />🎥 Video: {banner.videoUrl}</>}
                        </p>
                      )}
                      <div className="banner-status">
                        <label className="status-toggle">
                          <input
                            type="checkbox"
                            checked={banner.active}
                            onChange={() => toggleActive(banner.id, banner.active)}
                          />
                          <span>{banner.active ? '✓ Active' : '✗ Inactive'}</span>
                        </label>
                      </div>
                      <div className="banner-actions">
                        <button className="edit-btn" onClick={() => handleEdit(banner)}>
                          Edit
                        </button>
                        <button className="delete-btn" onClick={() => handleDelete(banner.id, banner.mediaUrl)}>
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Add/Edit Banner Modal */}
        {showAddModal && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal-content banner-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{editingBanner ? 'Edit Banner' : 'Add New Banner'}</h2>
                <button className="close-btn" onClick={closeModal}>✕</button>
              </div>
              <form onSubmit={handleSubmit} className="banner-form">
                <div className="form-group">
                  <label>Media Type *</label>
                  <div className="radio-group">
                    <label>
                      <input
                        type="radio"
                        value="image"
                        checked={formData.mediaType === 'image'}
                        onChange={(e) => setFormData({ ...formData, mediaType: e.target.value })}
                      />
                      📷 Image
                    </label>
                    <label>
                      <input
                        type="radio"
                        value="video"
                        checked={formData.mediaType === 'video'}
                        onChange={(e) => setFormData({ ...formData, mediaType: e.target.value })}
                      />
                      🎥 Video
                    </label>
                  </div>
                </div>

                <div className="form-group">
                  <label>Upload {formData.mediaType === 'video' ? 'Video' : 'Image'} *</label>
                  <input
                    type="file"
                    accept={formData.mediaType === 'video' ? 'video/*' : 'image/*'}
                    onChange={handleFileChange}
                  />
                  {editingBanner && !formData.mediaFile && (
                    <p className="current-media">Current: {formData.mediaUrl.split('/').pop()}</p>
                  )}
                </div>

                <div className="form-group">
                  <label>Tagline (Main Text) *</label>
                  <input
                    type="text"
                    placeholder="e.g., Cinematic Excellence"
                    value={formData.tagline}
                    onChange={(e) => setFormData({ ...formData, tagline: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Subtitle (Optional)</label>
                  <input
                    type="text"
                    placeholder="e.g., Creating Unforgettable Visual Stories"
                    value={formData.subtitle}
                    onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.showButton}
                      onChange={(e) => setFormData({ ...formData, showButton: e.target.checked })}
                    />
                    Show Button
                  </label>
                </div>

                {formData.showButton && (
                  <>
                    <div className="form-group">
                      <label>Button Text *</label>
                      <input
                        type="text"
                        placeholder="e.g., Our Productions"
                        value={formData.buttonText}
                        onChange={(e) => setFormData({ ...formData, buttonText: e.target.value })}
                        required={formData.showButton}
                      />
                    </div>

                    <div className="form-group">
                      <label>Button Link *</label>
                      <input
                        type="text"
                        placeholder="e.g., #projects or /film-production"
                        value={formData.buttonLink}
                        onChange={(e) => setFormData({ ...formData, buttonLink: e.target.value })}
                        required={formData.showButton}
                      />
                      <p className="help-text">Link to section (#projects) or page (/film-production)</p>
                    </div>

                    <div className="form-group">
                      <label>Video URL (Optional)</label>
                      <input
                        type="text"
                        placeholder="e.g., https://www.youtube.com/watch?v=..."
                        value={formData.videoUrl}
                        onChange={(e) => setFormData({ ...formData, videoUrl: e.target.value })}
                      />
                      <p className="help-text">YouTube or any video URL to open when button is clicked</p>
                    </div>
                  </>
                )}

                <div className="form-group">
                  <label>Priority (Display Order) *</label>
                  <input
                    type="number"
                    min="1"
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                    required
                  />
                  <p className="help-text">Lower numbers appear first (1 = first slide)</p>
                </div>

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.active}
                      onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                    />
                    Active (Show on website)
                  </label>
                </div>

                <div className="form-actions">
                  <button type="button" className="cancel-btn" onClick={closeModal}>
                    Cancel
                  </button>
                  <button type="submit" className="submit-btn" disabled={uploading}>
                    {uploading ? 'Saving...' : editingBanner ? 'Update Banner' : 'Add Banner'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Dashboard>
  );
};

export default BannerManager;
